import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { GridReadyEvent } from '@ag-grid-community/core';
import { AgFns, GridOptionsExt, GuidelineOverrideGrid, ISortModel, UtilDialogService } from '@core';
import { ProgramFrmComponent } from './program-frm.component';
import { AccountDbQueryService } from '../services/account-db-query.service';
import { AccountDbSaveService } from '../services/account-db-save.service';

/**
 * AI Guidelines tab of the Program form - GlobalAdmins only, like the rules themselves.
 *
 * One row per master. This tab writes no new rules: it can only override a master for this
 * program, once. Each row shows the overrides above (supplier, then the account) so the admin sees
 * the whole chain the AI will read for this program.
 */
@Component({
  selector: 'prox-program-guidelines-sub',
  templateUrl: './program-guidelines.sub.component.html',
  standalone: false,
})
export class ProgramGuidelinesSubComponent implements OnInit, OnChanges {
  @Input() parent!: ProgramFrmComponent;
  @Input() visible!: boolean;

  gridOptions: GridOptionsExt = {};
  grid: GuidelineOverrideGrid;
  private isLoaded = false;

  constructor(
    public dbQueryService: AccountDbQueryService,
    public dbSaveService: AccountDbSaveService,
    public dialogService: UtilDialogService,
  ) {
    this.grid = new GuidelineOverrideGrid(
      this.dbSaveService,
      () => ({ level: 'program', id: this.program.id, accountId: this.program.accountId }),
      () => this.isReadOnly(),
    );
    this.gridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onGridReady,
      getRowId: (params) => params.data.master.id,
    });
  }

  get program() {
    return this.parent.program;
  }

  ngOnInit() {
    this.refresh();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.refresh();
  }

  async refresh(forceRefresh = false) {
    if (!this.visible && !forceRefresh) return;
    this.parent.updateComponentNameMap(this);
    if (this.isLoaded && !forceRefresh) return;
    if (this.parent.isBeingAdded) {
      // A new program (or a new cycle) has no saved overrides yet; they show after the first save.
      this.grid.setGuidelines([]);
      this.isLoaded = false;
      AgFns.refreshGrid(this.gridOptions, this.grid.rows);
      return;
    }
    await this.parent.wrapBusy(async () => {
      this.grid.setGuidelines(await this.dbQueryService.getGuidelinesForProgram(this.program.id));
      this.isLoaded = true;
      AgFns.refreshGrid(this.gridOptions, this.grid.rows);
    });
  }

  onGridReady(event: GridReadyEvent) {
    const sortModel: ISortModel = [{ colId: 'master', sort: 'asc' }];
    AgFns.initGrid(event, this.grid.colDefs(), sortModel);
  }

  /** Called by the Program form before it saves. False = do not save. */
  async beforeSave(): Promise<boolean> {
    const problems = this.grid.problems();
    if (problems.length > 0) {
      await this.dialogService.okDialog('Cannot save the AI Guidelines', problems.join('<br/>'));
      return false;
    }
    return true;
  }

  isReadOnly() {
    return this.parent.isReadOnly() || !this.parent.authUser?.isGlobalAdmin;
  }
}
